import React, { useEffect, useRef, useState } from 'react';
import { 
  FaLaptop, FaBus, FaArrowRight, FaGraduationCap, FaTree, FaSun, FaStar,
  FaHeartbeat
} from 'react-icons/fa';
import { 
  MdEmojiEvents
} from 'react-icons/md';
import { 
  GiBookshelf, GiMicroscope, GiCricketBat
} from 'react-icons/gi';

const FacilitiesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const facilities = [
    {
      name: "Central Library",
      icon: GiBookshelf,
      color: "from-blue-600 to-indigo-500",
      description: "A treasure trove of knowledge with over 10,000+ books, journals, and digital resources. Our library provides a serene environment for research and self-study."
    },
    {
      name: "Computer Lab",
      icon: FaLaptop,
      color: "from-green-500 to-emerald-500",
      description: "State-of-the-art computer labs with 100+ high-end systems, high-speed internet connectivity, and the latest software for hands-on experience."
    },
    {
      name: "Sports Complex",
      icon: GiCricketBat,
      color: "from-orange-500 to-red-500",
      description: "A sprawling sports complex featuring cricket ground, basketball court, volleyball court, and indoor games facilities with professional coaches."
    },
    {
      name: "Transportation",
      icon: FaBus,
      color: "from-blue-300 to-blue-400",
      description: "Safe and reliable transportation services with a fleet of buses covering all major routes, ensuring punctual and secure travel."
    },
    {
      name: "Science Laboratories",
      icon: GiMicroscope,
      color: "from-cyan-500 to-teal-500",
      description: "Well-equipped laboratories for Physics, Chemistry, Biology, and Computer Science with modern equipment under expert faculty guidance."
    }
  ];

  const featuredFacility = facilities[0];

  return (
    <section ref={sectionRef} className="py-16 px-6 md:px-10 lg:px-12 bg-gradient-to-b from-red-50/30 via-red to-red-50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm mb-3">
            <FaHeartbeat className="text-indigo-600 text-sm" />
            <span className="text-indigo-600 font-semibold text-sm">Campus Experience</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800">
            Where Learning Meets
            <span className="block bg-gradient-to-r from-indigo-600 via-blue-400 to-blue-600 bg-clip-text text-transparent">
              World-Class Facilities
            </span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-indigo-500 via-blue-300 to-blue-400 mx-auto rounded-full mt-4"></div>
        </div>

        {/* Featured Facility */}
        <div className={`mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="relative bg-gradient-to-br from-indigo-600 to-blue-400 rounded-2xl overflow-hidden shadow-xl">
            <div className="relative p-6 md:p-8 text-white">
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full mb-3">
                    <FaStar className="text-yellow-400 text-xs" />
                    <span className="text-xs font-semibold">Featured</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-2">{featuredFacility.name}</h3>
                  <p className="text-white/90 text-sm leading-relaxed mb-4">{featuredFacility.description}</p>
                  <button className="group bg-white text-indigo-600 px-5 py-2 rounded-lg font-semibold text-sm hover:shadow-lg transition-all duration-300 flex items-center gap-2">
                    Explore More
                    <FaArrowRight className="group-hover:translate-x-1 transition-transform text-xs" />
                  </button>
                </div>
                <div className="hidden md:block text-center">
                  <featuredFacility.icon className="text-6xl mb-2 opacity-80" />
                  <p className="text-xs text-white/80">Premium Facility • Available 24/7</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Facilities Grid */}
        <div className={`grid md:grid-cols-2 gap-5 mb-12 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {facilities.slice(1).map((facility, idx) => (
            <div
              key={idx}
              className="group bg-white rounded-xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100"
            >
              <div className="flex gap-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${facility.color} rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                  <facility.icon className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1 group-hover:text-indigo-600 transition-colors">
                    {facility.name}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {facility.description}
                  </p>
                  <div className="mt-2 pt-2 border-t border-gray-100 flex gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <FaSun className="text-yellow-500 text-xs" /> Available Daily
                    </span>
                    <span className="flex items-center gap-1">
                      <FaGraduationCap className="text-indigo-500 text-xs" /> Student Friendly
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FacilitiesSection;